/* eslint-disable complexity */
import React, { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { Icon, Typography } from '@scale/scaleui';
import {
  OnboardingFlowNode,
  OnboardingFlowStepNode,
  StepStatsResponse,
  OnboardingStepType,
  ScheduleInterviewStepConfig,
  InterviewOutcomeStepConfig,
  FlowTerminusDestinationType,
  OnboardingFlowTerminusNode,
  CourseStepConfig,
  OnboardingFlowTransition,
  AsyncInterviewStepConfig,
  SkillScreeningStepConfig,
  CanonicalScreeningConfig,
  ResumeScreenStepConfig,
} from '@scale/shared/onboardingControlPanel';
import PanelEntry from '../PanelEntry';
import DetailPanelSignupFlow from './DetailPanelSignupFlow';
import DetailPanelResumeScreen from './DetailPanelResumeScreen';
import {
  useOCPStatsStepActiveUsers,
  useOCPStatsStepConversionRate,
  useOCPStatsStepAverageTimeSpent,
  fetchInterview,
  fetchScreening,
} from '../../api';
import CopyToClipboard from 'react-copy-to-clipboard';
import { toast } from 'react-toastify';
import { isTerminusNode } from '../../config/config';
import apiFetch from 'react-scale/lib/api-fetch';
import { useOCPStore } from 'react-scale/components/GrowthCenter/OnboardingControlPanel/hooks/useOCPStore';
import UsersOnStep from 'react-scale/components/GrowthCenter/OnboardingControlPanel/components/UsersOnStep';

interface Props {
  flowId: string;
  flowName: string;
  node: OnboardingFlowNode;
  stepStats: StepStatsResponse;
  loadingStepStats: boolean;
}

const DetailPanelNodeView: React.FC<Props> = ({
  flowId,
  flowName,
  node,
  stepStats,
  loadingStepStats,
}) => {
  const { currentFlow, flowFilterTags, setDetailTarget } = useOCPStore(state => ({
    currentFlow: state.currentFlow,
    flowFilterTags: state.flowFilterTags,
    setDetailTarget: state.setDetailTarget,
  }));

  const tagValues = flowFilterTags ? flowFilterTags.map(tag => tag.value) : [];
  const { isLoading: isLoadingActiveUsers, data: activeUsersData } = useOCPStatsStepActiveUsers({
    flowId: flowId.startsWith('tempId_') ? '' : flowId,
    step: node.name,
    tags: tagValues,
  });
  const { isLoading: isLoadingConversionRate, data: conversionRateData } =
    useOCPStatsStepConversionRate({
      flowId: flowId.startsWith('tempId_') ? '' : flowId,
      step: node.name,
      tags: tagValues,
    });
  const { isLoading: isLoadingAverageTimeSpent, data: averageTimeSpentData } =
    useOCPStatsStepAverageTimeSpent({
      flowId: flowId.startsWith('tempId_') ? '' : flowId,
      step: node.name,
      tags: tagValues,
    });

  const [destinationTitle, setDestinationTitle] = useState<string>('');
  const [activeInterview, setActiveInterview] = useState<Record<string, any>>({});
  const [activeScreening, setActiveScreening] = useState<Record<string, any>>({});

  useEffect(() => {
    let interviewId = '';
    if ((node as OnboardingFlowStepNode).stepType === OnboardingStepType.ScheduleInterview) {
      interviewId = ((node as OnboardingFlowStepNode).config as ScheduleInterviewStepConfig)
        .interviewId;
    } else if ((node as OnboardingFlowStepNode).stepType === OnboardingStepType.InterviewOutcome) {
      interviewId = ((node as OnboardingFlowStepNode).config as InterviewOutcomeStepConfig)
        .interviewId;
    } else if ((node as OnboardingFlowStepNode).stepType === OnboardingStepType.AsyncInterview) {
      interviewId = ((node as OnboardingFlowStepNode).config as AsyncInterviewStepConfig)
        .interviewId;
    }
    if (interviewId) {
      fetchInterview(interviewId)
        .then(interview => {
          setActiveInterview(interview);
          return;
        })
        .catch(() => {});
    }

    if ((node as OnboardingFlowStepNode).stepType === OnboardingStepType.SkillScreening) {
      const config = (node as OnboardingFlowStepNode).config as SkillScreeningStepConfig;
      if (config.canonicalScreeningConfig === CanonicalScreeningConfig.GeneralReasoning) {
        return;
      }

      fetchScreening(config.screeningConfigId!)
        .then(interview => {
          setActiveScreening(interview);
          return;
        })
        .catch(() => {});
    }
  }, [node.name, flowId]);

  useEffect(() => {
    let destinationId = '';
    if (isTerminusNode(node)) {
      destinationId = (node as OnboardingFlowTerminusNode).destinationId ?? '';
    }
    if (
      destinationId &&
      (node as OnboardingFlowTerminusNode).destinationType ===
        FlowTerminusDestinationType.WorkerTeam
    ) {
      (async () => {
        try {
          const team = await apiFetch(null, `corp-api/worker_teams/team/${destinationId}`);
          if (team?.name) setDestinationTitle(team.name);
          // eslint-disable-next-line no-empty
        } catch {}
      })();
    }
  }, [node]);

  const notifyCopySuccess = () =>
    toast('Copied!', {
      position: 'bottom-center',
      autoClose: 1000,
      progressStyle: { background: 'green' },
    });

  const transitions =
    currentFlow?.edges.filter(
      (transition: OnboardingFlowTransition) =>
        transition.sourceNode === node.name || transition.destNode === node.name,
    ) ?? [];
  const sourceSteps: string[] = [
    ...new Set(
      transitions
        .filter((transition: OnboardingFlowTransition) => transition.destNode === node.name)
        .map((transition: OnboardingFlowTransition) => transition.sourceNode),
    ),
  ] as string[];
  const destSteps: string[] = [
    ...new Set(
      transitions
        .filter((transition: OnboardingFlowTransition) => transition.sourceNode === node.name)
        .map((transition: OnboardingFlowTransition) => transition.destNode),
    ),
  ] as string[];

  const isSignupNode = (node as OnboardingFlowStepNode).stepType === OnboardingStepType.Signup;

  return (
    <div>
      {isSignupNode && <DetailPanelSignupFlow ocpFlowId={currentFlow?._id} />}
      {(node as OnboardingFlowStepNode).stepType === OnboardingStepType.ResumeScreen && (
        <DetailPanelResumeScreen
          ocpFlowId={flowId}
          nodeResumeScreenConfigId={
            ((node as OnboardingFlowStepNode).config as ResumeScreenStepConfig)
              .resumeScreeningConfigId
          }
          nodeResumeScreenConfigNodeOnly={
            ((node as OnboardingFlowStepNode).config as ResumeScreenStepConfig)
              .resumeScreeningConfigNodeOnly
          }
        />
      )}
      {!isTerminusNode(node) && (
        <PanelEntry title="Overall Metrics">
          <MetricRow
            label="Current user count"
            format="round"
            value={activeUsersData?.result}
            isLoading={isLoadingActiveUsers}
          />
          <MetricRow
            label="All-time avg time spent (min)"
            format="round"
            value={averageTimeSpentData?.result}
            isLoading={isLoadingAverageTimeSpent}
          />
          <MetricRow
            label="All-time conversion"
            format="percentage"
            value={conversionRateData?.result}
            isLoading={isLoadingConversionRate}
          />
        </PanelEntry>
      )}

      <PanelEntry title="Metrics By Timeframe">
        <div className="mt-4">
          <div className="grid grid-cols-4 gap-4 text-neutral-700 text-xs">
            {/* Empty top-left cell */}
            <div></div>

            {/* Column Headers */}
            <div className="text-center">Last 24 Hours</div>
            <div className="text-center">Last 7 Days</div>
            <div className="text-center">All Time</div>
            {/* Rows for each Metric */}

            {!isTerminusNode(node) && (
              <>
                <div>Conversion</div>
                <MetricGridEntry
                  value={stepStats.usersExited24Hours / stepStats.usersEntered24Hours}
                  isLoading={loadingStepStats}
                  format={'percentage'}
                />
                <MetricGridEntry
                  value={stepStats.usersExited7Days / stepStats.usersEntered7Days}
                  isLoading={loadingStepStats}
                  format={'percentage'}
                />
                <MetricGridEntry
                  value={stepStats.usersExitedAllTime / stepStats.usersEnteredAllTime}
                  isLoading={loadingStepStats}
                  format={'percentage'}
                />
              </>
            )}
            <div>Entered</div>
            <MetricGridEntry
              value={stepStats.usersEntered24Hours}
              isLoading={loadingStepStats}
              format={'round'}
            />
            <MetricGridEntry
              value={stepStats.usersEntered7Days}
              isLoading={loadingStepStats}
              format={'round'}
            />
            <MetricGridEntry
              value={stepStats.usersEnteredAllTime}
              isLoading={loadingStepStats}
              format={'round'}
            />
            {!isTerminusNode(node) && (
              <>
                <div>Exited</div>
                <MetricGridEntry
                  value={stepStats.usersExited24Hours}
                  isLoading={loadingStepStats}
                  format={'round'}
                />
                <MetricGridEntry
                  value={stepStats.usersExited7Days}
                  isLoading={loadingStepStats}
                  format={'round'}
                />
                <MetricGridEntry
                  value={stepStats.usersExitedAllTime}
                  isLoading={loadingStepStats}
                  format={'round'}
                />
              </>
            )}
          </div>
        </div>
      </PanelEntry>

      <PanelEntry title="Details">
        {(node as OnboardingFlowStepNode).stepType && (
          <div className="flex justify-between gap-2">
            <Typography.Paragraph3 className="whitespace-nowrap">Step Type</Typography.Paragraph3>
            <Typography.Paragraph3 className="text-right">
              {(node as OnboardingFlowStepNode).stepType}
            </Typography.Paragraph3>
          </div>
        )}
        {node.externalName && (
          <div className="flex justify-between gap-2">
            <Typography.Paragraph3 className="whitespace-nowrap">
              Contributor-Facing Name
            </Typography.Paragraph3>
            <Typography.Paragraph3 className="text-right">
              {node.externalName}
            </Typography.Paragraph3>
          </div>
        )}
        {node.externalDescription && (
          <div className="flex justify-between gap-2">
            <Typography.Paragraph3 className="whitespace-nowrap">
              Contributor-Facing Description
            </Typography.Paragraph3>
            <Typography.Paragraph3 className="text-right">
              {node.externalDescription}
            </Typography.Paragraph3>
          </div>
        )}
        {((node as OnboardingFlowStepNode).stepType === OnboardingStepType.ScheduleInterview ||
          (node as OnboardingFlowStepNode).stepType === OnboardingStepType.InterviewOutcome ||
          (node as OnboardingFlowStepNode).stepType === OnboardingStepType.AsyncInterview) && (
          <>
            <div className="flex justify-between gap-2">
              <Typography.Paragraph3 className="whitespace-nowrap">
                Interview ID
              </Typography.Paragraph3>
              <Typography.Paragraph3 className="text-right">
                {(activeInterview._id ?? '').toString()}
              </Typography.Paragraph3>
            </div>
            <div className="flex justify-between gap-2">
              <Typography.Paragraph3 className="whitespace-nowrap">
                Interview Name
              </Typography.Paragraph3>
              <Typography.Paragraph3 className="text-right">
                {activeInterview.name}
              </Typography.Paragraph3>
            </div>
            <div className="flex justify-between gap-2">
              <Typography.Paragraph3 className="whitespace-nowrap">
                Outcome Options
              </Typography.Paragraph3>
              <Typography.Paragraph3 className="text-right">
                {(activeInterview.interviewOutcomeList ?? []).length > 1
                  ? activeInterview.interviewOutcomeList.join(', ')
                  : ''}
              </Typography.Paragraph3>
            </div>
            {(node as OnboardingFlowStepNode).stepType === OnboardingStepType.ScheduleInterview && (
              <>
                <div className="flex justify-between gap-2">
                  <Typography.Paragraph3 className="whitespace-nowrap">
                    Calendly Link
                  </Typography.Paragraph3>
                  <Typography.Paragraph3 className="text-right">
                    <a
                      href={activeInterview.calendlyLink}
                      className={'url-text-'}
                      style={{ color: '#6A5DF9' }}
                    >
                      {activeInterview.calendlyLink}
                    </a>
                  </Typography.Paragraph3>
                </div>
                <div className="flex justify-between gap-2">
                  <Typography.Paragraph3 className="whitespace-nowrap">
                    Interview Course ID
                  </Typography.Paragraph3>
                  <Typography.Paragraph3 className="text-right">
                    {activeInterview.courseId}
                  </Typography.Paragraph3>
                </div>
                <div className="flex justify-between gap-2">
                  <Typography.Paragraph3 className="whitespace-nowrap">
                    Tailing Google Sheets
                  </Typography.Paragraph3>
                  <Typography.Paragraph3 className="text-right">
                    <a
                      href={
                        'https://docs.google.com/spreadsheets/d/' +
                        (activeInterview.tailingGoogleSheetsId ?? '')
                      }
                      className={'url-text-'}
                      style={{ color: '#6A5DF9' }}
                    >
                      {activeInterview.tailingGoogleSheetsId
                        ? 'https://docs.google.com/spreadsheets/d/'
                        : ''}
                      <br />
                      {activeInterview.tailingGoogleSheetsId ?? ''}
                    </a>
                  </Typography.Paragraph3>
                </div>
              </>
            )}
            {(node as OnboardingFlowStepNode).stepType === OnboardingStepType.AsyncInterview && (
              <>
                <div className="flex justify-between gap-2">
                  <Typography.Paragraph3 className="whitespace-nowrap">
                    Willo Interview ID
                  </Typography.Paragraph3>
                  <Typography.Paragraph3 className="text-right">
                    <a
                      href={`https://app.willotalent.com/jobs/overview/${activeInterview.willoInterviewId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={'url-text-'}
                      style={{ color: '#6A5DF9' }}
                    >
                      {activeInterview.willoInterviewId}
                    </a>
                  </Typography.Paragraph3>
                </div>
                <div className="flex justify-between gap-2">
                  <Typography.Paragraph3 className="whitespace-nowrap">
                    Willo Interview Owner
                  </Typography.Paragraph3>
                  <Typography.Paragraph3 className="text-right">
                    {activeInterview.willoInterviewOwner}
                  </Typography.Paragraph3>
                </div>
                <div className="flex justify-between gap-2">
                  <Typography.Paragraph3 className="whitespace-nowrap">
                    Interview Active?
                  </Typography.Paragraph3>
                  <Typography.Paragraph3 className="text-right">
                    {activeInterview.isActive ? 'LIVE' : 'CLOSED'}
                  </Typography.Paragraph3>
                </div>
              </>
            )}
          </>
        )}
        {(node as OnboardingFlowStepNode).stepType === OnboardingStepType.SkillScreening && (
          <>
            <div className="flex justify-between gap-2">
              <Typography.Paragraph3 className="whitespace-nowrap">
                Screening ID
              </Typography.Paragraph3>
              <Typography.Paragraph3 className="text-right">
                {(activeScreening._id ?? '').toString()}
              </Typography.Paragraph3>
            </div>
            <div className="flex justify-between gap-2">
              <Typography.Paragraph3 className="whitespace-nowrap">
                Screening Name
              </Typography.Paragraph3>
              <Typography.Paragraph3 className="text-right">
                {activeScreening.title}
              </Typography.Paragraph3>
            </div>
            <div className="flex justify-between gap-2">
              <Typography.Paragraph3 className="whitespace-nowrap">
                Difficulty
              </Typography.Paragraph3>
              <Typography.Paragraph3 className="text-right">
                {((node as OnboardingFlowStepNode).config as SkillScreeningStepConfig)
                  ?.fixedDifficulty ?? 'Adaptive Difficulty'}
              </Typography.Paragraph3>
            </div>
          </>
        )}
        {(node as OnboardingFlowStepNode).stepType === OnboardingStepType.Course ||
          ((node as OnboardingFlowStepNode).stepType === OnboardingStepType.PlatformCourse && (
            <>
              <div className="flex justify-between gap-2">
                <Typography.Paragraph3 className="whitespace-nowrap">
                  Course Name
                </Typography.Paragraph3>
                <Typography.Paragraph3 className="text-right">
                  {((node as OnboardingFlowStepNode).config as CourseStepConfig)?.courseTitle}
                </Typography.Paragraph3>
              </div>
              <div className="flex justify-between gap-2 items-center">
                <Typography.Paragraph3 className="whitespace-nowrap">
                  Course ID
                </Typography.Paragraph3>
                {((node as OnboardingFlowStepNode).config as CourseStepConfig)?.courseId && (
                  <div className="flex gap-1 items-center">
                    <Typography.Paragraph3 className="text-right">
                      {((node as OnboardingFlowStepNode).config as CourseStepConfig)?.courseId}
                    </Typography.Paragraph3>
                    <CopyToClipboard
                      text={((node as OnboardingFlowStepNode).config as CourseStepConfig)?.courseId}
                      onCopy={notifyCopySuccess}
                    >
                      <Icon icon="copy" variant="neutral" size="sm" />
                    </CopyToClipboard>
                  </div>
                )}
              </div>
            </>
          ))}
        {isTerminusNode(node) &&
          (node as OnboardingFlowTerminusNode).destinationType ===
            FlowTerminusDestinationType.WorkerTeam && (
            <>
              <div className="flex justify-between gap-2">
                <Typography.Paragraph3 className="whitespace-nowrap">
                  Worker Team Name
                </Typography.Paragraph3>
                <Typography.Paragraph3 className="text-right">
                  {destinationTitle}
                </Typography.Paragraph3>
              </div>
              <div className="flex justify-between gap-2 items-center">
                <Typography.Paragraph3 className="whitespace-nowrap">
                  Worker Team ID
                </Typography.Paragraph3>
                {(node as OnboardingFlowTerminusNode)?.destinationId && (
                  <div className="flex gap-1 items-center">
                    <Typography.Paragraph3 className="text-right">
                      {(node as OnboardingFlowTerminusNode)?.destinationId}
                    </Typography.Paragraph3>
                    <CopyToClipboard
                      text={(node as OnboardingFlowTerminusNode)?.destinationId ?? ''}
                      onCopy={notifyCopySuccess}
                    >
                      <Icon icon="copy" variant="neutral" size="sm" />
                    </CopyToClipboard>
                  </div>
                )}
              </div>
            </>
          )}
      </PanelEntry>
      {!!sourceSteps?.length && (
        <PanelEntry title="Source Step(s)">
          {sourceSteps.map((sourceStep: string) => (
            <div key={sourceStep}>
              <div className="flex items-center">
                <div>
                  <Typography.Paragraph2>{sourceStep}</Typography.Paragraph2>
                </div>
              </div>
              <div className="flex gap-2 items-center">
                <Typography.Paragraph3>via</Typography.Paragraph3>
                <div className="flex flex-col">
                  {transitions
                    .filter((t: OnboardingFlowTransition) => t.sourceNode === sourceStep)
                    .map((t: OnboardingFlowTransition) => (
                      <div key={t.name} className="flex items-center gap-1">
                        <Typography.Paragraph3>{t.name}</Typography.Paragraph3>
                        <Icon
                          icon="arrow-up-right-from-square"
                          size="sm"
                          onClick={() => setDetailTarget(t)}
                          tooltip="View Edge"
                        />
                      </div>
                    ))}
                </div>
              </div>
            </div>
          ))}
        </PanelEntry>
      )}
      {!!destSteps?.length && (
        <PanelEntry title="Destination Step(s)">
          {destSteps.map((destStep: string) => (
            <div key={destStep}>
              <div className="flex items-center">
                <div>
                  <Typography.Paragraph2>{destStep}</Typography.Paragraph2>
                </div>
              </div>
              <div className="flex gap-2 items-center">
                <Typography.Paragraph3>via</Typography.Paragraph3>
                <div className="flex flex-col">
                  {transitions
                    .filter((t: OnboardingFlowTransition) => t.destNode === destStep)
                    .map((t: OnboardingFlowTransition) => (
                      <div key={t.name} className="flex items-center gap-1">
                        <Typography.Paragraph3>{t.name}</Typography.Paragraph3>
                        <Icon
                          icon="arrow-up-right-from-square"
                          size="sm"
                          onClick={() => setDetailTarget(t)}
                          tooltip="View Edge"
                        />
                      </div>
                    ))}
                </div>
              </div>
            </div>
          ))}
        </PanelEntry>
      )}

      <UsersOnStep
        flowId={flowId}
        flowName={flowName}
        node={node}
        activeUsersData={activeUsersData}
      />
    </div>
  );
};

const formatValue = (value: number | undefined, format: string, isLoading: boolean) => {
  if (isLoading) return 'loading...';
  if (!_.isNil(value) && format === 'round') return Math.round(value);
  if (!_.isNil(value) && format === 'percentage') return `${Math.round(value * 100)}%`;
  return 'N/A';
};

const MetricRow = ({
  label,
  value,
  isLoading,
  format,
}: {
  label: string;
  value?: number;
  isLoading: boolean;
  format: 'round' | 'percentage';
}) => {
  const formattedValue = useMemo(() => {
    return formatValue(value, format, isLoading);
  }, [isLoading, value, format]);

  return (
    <div className="flex justify-between">
      <Typography.Paragraph3>{label}</Typography.Paragraph3>
      <Typography.Paragraph3>{formattedValue}</Typography.Paragraph3>
    </div>
  );
};

const MetricGridEntry = ({
  value,
  isLoading,
  format,
}: {
  value?: number;
  isLoading: boolean;
  format: 'round' | 'percentage';
}) => {
  const formattedValue = useMemo(() => {
    return formatValue(value, format, isLoading);
  }, [isLoading, value, format]);

  return <div className="text-center">{formattedValue}</div>;
};

export default DetailPanelNodeView;
